/**
 * Masking Configuration
 *
 * Centralized config for auto-masking sensitive fields.
 * Maps field names to masking types so any dynamically rendered field
 * is automatically masked based on its name or label.
 */

// Exact field name → masking type
const EXACT_FIELD_MAP = {
    'full_name': 'name',
    'first_name': 'name',
    'last_name': 'name',
    'middle_name': 'name',
    'name': 'name',
    'father_name': 'name',
    'mother_name': 'name',
    'spouse_name': 'name',
    'co_applicant_name': 'name',
    'employee_name': 'name',
    'applicant_name': 'name',
    'guarantor_name': 'name',
    'nominee_name': 'name',
    'phone': 'phone',
    'mobile': 'phone',
    'mobile_number': 'phone',
    'phone_number': 'phone',
    'contact_number': 'phone',
    'alternate_phone': 'phone',
    'alternate_mobile': 'phone',
    'reference_phone': 'phone',
    'emergency_contact': 'phone',
    'email': 'email',
    'email_address': 'email',
    'alternate_email': 'email',
    'pan': 'pan',
    'pan_number': 'pan',
    'pan_card': 'pan',
    'aadhaar': 'aadhaar',
    'aadhaar_number': 'aadhaar',
    'aadhar': 'aadhaar',
    'aadhar_number': 'aadhaar',
    'aadhar_no': 'aadhaar',
    'dob': 'dob',
    'date_of_birth': 'dob',
    'birth_date': 'dob',
    'address': 'address',
    'current_address': 'address',
    'permanent_address': 'address',
    'residential_address': 'address',
    'office_address': 'address',
    'communication_address': 'address',
    'bank_account_number': 'bank_account',
    'account_number': 'bank_account',
    'bank_account': 'bank_account',
    'voter_id': 'generic',
    'voter_id_number': 'generic',
    'voter_number': 'generic',
    'driving_licence': 'generic',
    'driving_licence_number': 'generic',
    'dl_number': 'generic',
    'passport_number': 'generic',
    'passport': 'generic',
    'ifsc': 'generic',
    'ifsc_code': 'generic',
    'uan': 'generic',
    'uan_number': 'generic',
    'pf_number': 'generic',
    'epf_number': 'generic',
    'gst_number': 'generic',
    'gstin': 'generic',
    'cin': 'generic',
    'din': 'generic',
};

// Keyword patterns — if field name CONTAINS any of these, apply the mask type
const KEYWORD_PATTERNS = [
    { keywords: ['name', 'nama'], type: 'name', exclude: ['company_name', 'firm_name', 'business_name', 'product_name', 'bank_name', 'lender_name', 'employer_name', 'designation', 'loan_type'] },
    { keywords: ['phone', 'mobile', 'contact_no', 'whatsapp'], type: 'phone' },
    { keywords: ['email'], type: 'email' },
    { keywords: ['pan_'], type: 'pan' },
    { keywords: ['aadhaar', 'aadhar'], type: 'aadhaar' },
    { keywords: ['address', 'addr'], type: 'address', exclude: ['city', 'state', 'pincode', 'pin_code'] },
    { keywords: ['account_no', 'account_num', 'acct_no', 'bank_acc'], type: 'bank_account' },
    { keywords: ['voter', 'licence', 'license', 'passport', 'uan_', 'pf_no', 'epf_', 'gst_no', 'gstin', 'din_'], type: 'generic' },
    { keywords: ['dob', 'birth_date', 'date_of_birth'], type: 'dob' },
];

// Label-based patterns — if the field LABEL contains these words
const LABEL_PATTERNS = [
    { keywords: ['name'], type: 'name', exclude: ['company', 'firm', 'business', 'product', 'bank', 'lender', 'employer', 'designation', 'loan type'] },
    { keywords: ['phone', 'mobile', 'contact'], type: 'phone' },
    { keywords: ['email'], type: 'email' },
    { keywords: ['pan'], type: 'pan', exclude: ['panel', 'expand'] },
    { keywords: ['aadhaar', 'aadhar'], type: 'aadhaar' },
    { keywords: ['address'], type: 'address' },
    { keywords: ['account number', 'account no'], type: 'bank_account' },
    { keywords: ['voter', 'licence', 'license', 'passport', 'uan', 'gstin'], type: 'generic' },
    { keywords: ['date of birth', 'dob'], type: 'dob' },
];

/**
 * Masking functions — each takes a string value and returns the masked version
 */
const MASKING_FUNCTIONS = {
    name: (val) => {
        if (!val) return val;
        return String(val).trim().split(/\s+/).map(part =>
            part.length <= 1 ? part : part[0] + '***'
        ).join(' ');
    },
    phone: (val) => {
        if (!val) return val;
        const cleaned = String(val).replace(/\D/g, '');
        if (cleaned.length < 4) return 'XXXXXXXXXX';
        return 'XXXXX X' + cleaned.slice(-4);
    },
    email: (val) => {
        if (!val) return val;
        const parts = String(val).split('@');
        if (parts.length !== 2) return '***@***.***';
        const name = parts[0];
        return name.length <= 2
            ? name[0] + '***@' + parts[1]
            : name[0] + '***' + name[name.length - 1] + '@' + parts[1];
    },
    pan: (val) => {
        if (!val) return val;
        const s = String(val);
        if (s.length < 4) return 'XXXXXXXXXX';
        return 'XXXXX' + s.slice(-5, -1) + 'X';
    },
    aadhaar: (val) => {
        if (!val) return val;
        const cleaned = String(val).replace(/\D/g, '');
        return cleaned.length < 4 ? 'XXXX-XXXX-XXXX' : 'XXXX-XXXX-' + cleaned.slice(-4);
    },
    dob: (val) => {
        if (!val) return val;
        const str = String(val);
        const isoMatch = str.match(/(\d{4})-\d{2}-\d{2}/);
        if (isoMatch) return 'XX/XX/' + isoMatch[1];
        const dmyMatch = str.match(/\d{2}[/\-]\d{2}[/\-](\d{4})/);
        if (dmyMatch) return 'XX/XX/' + dmyMatch[1];
        // Try "18 Mar 2026" format
        const namedMonth = str.match(/\d{1,2}\s+\w+\s+(\d{4})/);
        if (namedMonth) return 'XX XXX ' + namedMonth[1];
        return 'XX/XX/XXXX';
    },
    address: (val) => {
        if (!val) return val;
        const str = String(val).trim();
        if (str.length <= 5) return 'X'.repeat(str.length);
        const visibleEnd = Math.min(8, Math.floor(str.length * 0.3));
        return 'X'.repeat(str.length - visibleEnd) + str.slice(-visibleEnd);
    },
    bank_account: (val) => {
        if (!val) return val;
        const cleaned = String(val).replace(/\D/g, '');
        if (cleaned.length < 4) return 'XXXXXXXXXXXX';
        return 'X'.repeat(cleaned.length - 4) + cleaned.slice(-4);
    },
    generic: (val) => {
        if (!val) return val;
        const s = String(val);
        if (s.length <= 4) return 'X'.repeat(s.length);
        return s.slice(0, 2) + 'X'.repeat(s.length - 4) + s.slice(-2);
    },
};

/**
 * Detect the masking type for a field based on its name and/or label.
 * @param {string} fieldName - The field key/name (e.g., 'full_name', 'pan_number')
 * @param {string} [fieldLabel] - The display label (e.g., 'Full Name', 'PAN Number')
 * @returns {string|null} - Masking type or null if not sensitive
 */
export const detectMaskingType = (fieldName, fieldLabel = '') => {
    if (!fieldName) return null;
    const lowerName = fieldName.toLowerCase();

    // 1. Exact match
    if (EXACT_FIELD_MAP[lowerName]) {
        return EXACT_FIELD_MAP[lowerName];
    }

    // 2. Keyword match on field name
    for (const pattern of KEYWORD_PATTERNS) {
        const matches = pattern.keywords.some(kw => lowerName.includes(kw));
        if (matches) {
            const excluded = pattern.exclude?.some(ex => lowerName.includes(ex));
            if (!excluded) return pattern.type;
        }
    }

    // 3. Label-based match
    if (fieldLabel) {
        const lowerLabel = fieldLabel.toLowerCase();
        for (const pattern of LABEL_PATTERNS) {
            const matches = pattern.keywords.some(kw => lowerLabel.includes(kw));
            if (matches) {
                const excluded = pattern.exclude?.some(ex => lowerLabel.includes(ex));
                if (!excluded) return pattern.type;
            }
        }
    }

    return null;
};

/**
 * Mask a field value based on its name/label.
 * @param {string} fieldName - The field key/name
 * @param {*} value - The field value to mask
 * @param {boolean} isMasked - Whether masking is currently active
 * @param {string} [fieldLabel] - Optional display label for label-based detection
 * @returns {*} - Masked value or original value
 */
export const maskFieldValue = (fieldName, value, isMasked, fieldLabel = '') => {
    if (!isMasked || !value || value === '—' || value === '-') return value;

    const maskType = detectMaskingType(fieldName, fieldLabel);
    if (!maskType) return value;

    const maskFn = MASKING_FUNCTIONS[maskType];
    if (!maskFn) return value;

    return maskFn(value);
};

/**
 * Get the masking function for a specific type.
 * @param {string} type - Masking type (name, phone, email, pan, aadhaar, dob, address, bank_account, generic)
 * @returns {function|null}
 */
export const getMaskingFunction = (type) => {
    return MASKING_FUNCTIONS[type] || null;
};

export default { detectMaskingType, maskFieldValue, getMaskingFunction, MASKING_FUNCTIONS };
